import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, Modal, Textarea } from '@/backoffice/components/ui';
import {
  fetchSupportRequests,
  updateSupportRequest,
  assignSupportRequest,
  type AdminSupportRequest,
} from '@/domains/admin/services/admin.service';
import { formatRelativeTime } from '@/backoffice/utils';
import { MessageSquare, Loader2, Check } from 'lucide-react';

type FilterStatus = 'all' | 'open' | 'in_progress' | 'resolved';

export function AdminSupportPage() {
  const [requests, setRequests] = useState<AdminSupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [selected, setSelected] = useState<AdminSupportRequest | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    fetchSupportRequests(filter === 'all' ? undefined : filter)
      .then(setRequests)
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [filter]);

  const handleResolve = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateSupportRequest(selected.id, { status: 'resolved', admin_notes: notes });
      setSelected(null);
      setNotes('');
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async (id: number) => {
    await assignSupportRequest(id);
    load();
  };

  return (
    <DashboardLayout userType="admin" title="Support client">
      <div className="flex gap-2 mb-6 flex-wrap">
        {(['all', 'open', 'in_progress', 'resolved'] as FilterStatus[]).map((f) => (
          <Button key={f} variant={filter === f ? 'primary' : 'outline'} size="sm" onClick={() => setFilter(f)}>
            {f === 'all' ? 'Toutes' : f === 'open' ? 'Ouvertes' : f === 'in_progress' ? 'En cours' : 'Résolues'}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : requests.length === 0 ? (
        <Card><p className="text-center text-gray-500 py-12">Aucune réclamation pour le moment.</p></Card>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <Card key={req.id}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3">
                  <MessageSquare className="w-5 h-5 text-indigo-600 mt-1" />
                  <div>
                    <p className="font-semibold">{req.subject}</p>
                    <p className="text-sm text-gray-600 mt-1">{req.message}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {req.user_name} · {req.created_at ? formatRelativeTime(req.created_at) : ''}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge variant={req.status === 'resolved' ? 'success' : req.status === 'in_progress' ? 'warning' : 'danger'}>
                    {req.status}
                  </Badge>
                  {req.status === 'open' && (
                    <Button size="sm" variant="outline" onClick={() => handleAssign(req.id)}>Prendre en charge</Button>
                  )}
                  {req.status !== 'resolved' && (
                    <Button size="sm" onClick={() => { setSelected(req); setNotes(req.admin_notes ?? ''); }}>Traiter</Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="Traiter la réclamation">
        {selected && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">{selected.message}</p>
            <Textarea label="Notes admin" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelected(null)}>Annuler</Button>
              <Button leftIcon={<Check className="w-4 h-4" />} isLoading={saving} onClick={handleResolve}>Marquer résolu</Button>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}
